import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RateReviewIcon from '@mui/icons-material/RateReview';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { fetchUsers, createUser, updateUser, deleteUser } from '../../api/admin';
import { fetchAllStudents } from '../../api/students';
import { fetchTutorProfile, updateTutorProfile } from '../../api/tutorProfile';
import UserFormDialog from '../../components/forms/UserFormDialog';
import ConfirmDialog from '../../components/feedback/ConfirmDialog';
import TutorReviewsDialog from '../../components/admin/TutorReviewsDialog';
import { ROLES } from '../../utils/roles';
import { SUBJECT_OPTIONS } from '../../components/forms/StudentFormDialog';

const B = { primary: '#4357AD', dark: '#1E2A4A', green: '#D3FEC6' };
const cardSx = { borderRadius: 4, boxShadow: '0 2px 12px rgba(67,87,173,0.08)', bgcolor: '#fff' };
const avatarColors = ['#4357AD', '#22C55E', '#F59E0B', '#EC4899', '#8B5CF6', '#06B6D4', '#EF4444', '#14B8A6'];

const TutorsPage = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedTutor, setSelectedTutor] = useState(null);
  const [confirmState, setConfirmState] = useState(null);
  const [reviewsTutor, setReviewsTutor] = useState(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const usersQuery = useQuery({ queryKey: ['users'], queryFn: fetchUsers });
  const studentsQuery = useQuery({ queryKey: ['students'], queryFn: fetchAllStudents });

  const tutorList = useMemo(
    () => (usersQuery.data || []).filter((user) => user.role === ROLES.TUTOR),
    [usersQuery.data]
  );

  const studentCounts = useMemo(() => {
    const counts = {};
    (studentsQuery.data || []).forEach((student) => {
      if (student.tutorId) counts[student.tutorId] = (counts[student.tutorId] || 0) + 1;
    });
    return counts;
  }, [studentsQuery.data]);

  const tutorSubjects = useMemo(() => {
    const map = {};
    (studentsQuery.data || []).forEach((student) => {
      if (student.tutorId && student.subjects) {
        if (!map[student.tutorId]) map[student.tutorId] = new Set();
        student.subjects.split(',').forEach((s) => {
          const trimmed = s.trim();
          if (trimmed) map[student.tutorId].add(trimmed);
        });
      }
    });
    // Convert Sets to arrays
    Object.keys(map).forEach((id) => { map[id] = [...map[id]]; });
    return map;
  }, [studentsQuery.data]);

  // Fetch tutor profiles to get their subjects
  const profileResults = useQueries({
    queries: tutorList.map((tutor) => ({
      queryKey: ['tutor-profile', tutor.id],
      queryFn: () => fetchTutorProfile(tutor.id),
      enabled: Boolean(tutor.id),
      staleTime: 60000,
    })),
  });

  // Normalize: convert label to value (e.g. "Математика" -> "mathematics")
  const normalizeSubject = (s) => {
    const byLabel = SUBJECT_OPTIONS.find((o) => o.label === s);
    if (byLabel) return byLabel.value;
    const byValue = SUBJECT_OPTIONS.find((o) => o.value === s);
    if (byValue) return byValue.value;
    return s;
  };

  const profileSubjectsMap = useMemo(() => {
    const map = {};
    profileResults.forEach((result) => {
      if (result.data?.tutorId && result.data?.subject) {
        const subs = result.data.subject.split(',').map((s) => normalizeSubject(s.trim())).filter(Boolean);
        map[result.data.tutorId] = subs;
      }
    });
    return map;
  }, [profileResults]);

  // Merge profile + student subjects, deduplicated
  const mergedSubjects = useMemo(() => {
    const map = {};
    tutorList.forEach((tutor) => {
      const fromProfile = profileSubjectsMap[tutor.id] || [];
      const fromStudents = (tutorSubjects[tutor.id] || []).map(normalizeSubject);
      map[tutor.id] = [...new Set([...fromProfile, ...fromStudents])];
    });
    return map;
  }, [tutorList, profileSubjectsMap, tutorSubjects]);

  const subjectLabel = (value) => SUBJECT_OPTIONS.find((o) => o.value === value)?.label || value;

  const createMutation = useMutation({
    mutationFn: (payload) => createUser({ ...payload, role: ROLES.TUTOR }),
    onSuccess: () => {
      enqueueSnackbar('Репетитора створено', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: () => enqueueSnackbar('Не вдалося створити репетитора', { variant: 'error' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) => updateUser(id, payload),
    onSuccess: () => {
      enqueueSnackbar('Дані оновлено', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: () => enqueueSnackbar('Не вдалося оновити дані', { variant: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteUser(id),
    onSuccess: () => {
      enqueueSnackbar('Репетитора видалено', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: () => enqueueSnackbar('Не вдалося видалити', { variant: 'error' }),
  });

  const isLoading = usersQuery.isLoading || studentsQuery.isLoading;

  const handleEditTutor = useCallback(async (tutor) => {
    setSelectedTutor(tutor);
    if (tutor) {
      try {
        const profile = await fetchTutorProfile(tutor.id);
        const subs = profile?.subject || '';
        setSelectedTutor((prev) => prev && { ...prev, profileSubjects: subs });
      } catch {}
    }
    setDialogOpen(true);
  }, []);

  const handleSubmit = async (values) => {
    const { subjects, ...userData } = values;
    if (selectedTutor) {
      await updateMutation.mutateAsync({ id: selectedTutor.id, payload: userData });
      if (subjects) {
        try {
          await updateTutorProfile(selectedTutor.id, { subject: subjects.join(', ') });
          queryClient.invalidateQueries({ queryKey: ['tutor-profile', selectedTutor.id] });
        } catch {}
      }
    } else {
      const created = await createMutation.mutateAsync(userData);
      if (subjects?.length && created?.id) {
        try {
          await updateTutorProfile(created.id, { subject: subjects.join(', ') });
        } catch {}
      }
    }
  };

  return (
    <Stack spacing={2.5}>
      {/* Header */}
      <Card sx={{ ...cardSx, background: `linear-gradient(135deg, ${B.dark} 0%, ${B.primary} 100%)` }}>
        <CardContent sx={{ py: 3, px: 3.5 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" gap={1.5}>
            <Box>
              <Typography variant="h5" fontWeight={800} sx={{ color: '#fff' }}>
                Репетитори
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mt: 0.5 }}>
                {tutorList.length} {tutorList.length === 1 ? 'репетитор' : 'репетиторів'}
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => { setSelectedTutor(null); setDialogOpen(true); }}
              sx={{
                bgcolor: 'rgba(255,255,255,0.15)',
                color: '#fff',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                borderRadius: 3,
                textTransform: 'none',
                fontWeight: 700,
                px: 2.5,
                alignSelf: { xs: 'flex-start', sm: 'auto' },
              }}
            >
              Додати репетитора
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Content */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: B.primary }} />
        </Box>
      ) : tutorList.length === 0 ? (
        <Card sx={cardSx}>
          <CardContent sx={{ py: 6, textAlign: 'center' }}>
            <PersonIcon sx={{ fontSize: 48, color: '#C4B5FD', mb: 1 }} />
            <Typography variant="h6" sx={{ color: B.dark }}>Репетиторів поки немає</Typography>
            <Typography variant="body2" sx={{ color: '#8891A5' }}>Додайте першого репетитора</Typography>
          </CardContent>
        </Card>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2.5 }}>
          {tutorList.map((tutor, i) => {
            const color = avatarColors[i % avatarColors.length];
            const count = studentCounts[tutor.id] || 0;
            const subjects = mergedSubjects[tutor.id] || [];
            return (
              <Card
                key={tutor.id}
                sx={{
                  ...cardSx,
                  border: '1.5px solid #EDE9FE',
                  transition: 'box-shadow 0.2s, transform 0.2s',
                  '&:hover': { boxShadow: '0 8px 30px rgba(67,87,173,0.15)', transform: 'translateY(-3px)' },
                }}
              >
                <CardContent sx={{ py: 2.5, px: 2.5 }}>
                  {/* Avatar + Name */}
                  <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                    <Avatar
                      sx={{
                        width: 48,
                        height: 48,
                        bgcolor: `${color}15`,
                        color,
                        fontWeight: 700,
                        fontSize: 18,
                        border: `2px solid ${color}30`,
                      }}
                    >
                      {tutor.name?.charAt(0)?.toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ color: B.dark }} noWrap>
                        {tutor.name}
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <EmailOutlinedIcon sx={{ fontSize: 14, color: '#8891A5' }} />
                        <Typography variant="caption" sx={{ color: '#8891A5' }} noWrap>
                          {tutor.email}
                        </Typography>
                      </Stack>
                    </Box>
                  </Stack>

                  {/* Stats + subjects */}
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                    <Chip
                      icon={<SchoolIcon sx={{ fontSize: 14 }} />}
                      label={`${count} учнів`}
                      size="small"
                      sx={{ bgcolor: '#EDE9FE', color: B.primary, fontWeight: 600, fontSize: 12, '& .MuiChip-icon': { color: B.primary } }}
                    />
                    {subjects.map((subj) => (
                      <Chip
                        key={subj}
                        label={subjectLabel(subj)}
                        size="small"
                        sx={{ bgcolor: '#D1FAE5', color: '#166534', fontWeight: 600, fontSize: 11 }}
                      />
                    ))}
                    {tutor.rawPassword && (
                      <Chip
                        label={tutor.rawPassword}
                        size="small"
                        sx={{ bgcolor: '#F8F7FF', color: '#8891A5', fontWeight: 500, fontSize: 11, fontFamily: 'monospace' }}
                      />
                    )}
                  </Stack>

                  {/* Actions */}
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    <Tooltip title="Редагувати">
                      <IconButton
                        size="small"
                        onClick={() => handleEditTutor(tutor)}
                        sx={{ color: B.primary, bgcolor: '#F0EDFF', '&:hover': { bgcolor: '#EDE9FE' } }}
                      >
                        <EditIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Профіль">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/admin/tutors/${tutor.id}/profile`)}
                        sx={{ color: '#22C55E', bgcolor: '#F0FDF4', '&:hover': { bgcolor: '#D1FAE5' } }}
                      >
                        <PersonIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Відгуки">
                      <IconButton
                        size="small"
                        onClick={() => setReviewsTutor(tutor)}
                        sx={{ color: '#F59E0B', bgcolor: '#FFFBEB', '&:hover': { bgcolor: '#FEF3C7' } }}
                      >
                        <RateReviewIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Видалити">
                      <IconButton
                        size="small"
                        onClick={() => setConfirmState(tutor)}
                        sx={{ color: '#EF4444', '&:hover': { bgcolor: '#FEE2E2' } }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      <UserFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleSubmit}
        initialValues={{
          name: selectedTutor?.name || '',
          email: selectedTutor?.email || '',
          password: selectedTutor?.rawPassword || '',
          subjects: selectedTutor?.profileSubjects || '',
        }}
        title={selectedTutor ? 'Редагувати репетитора' : 'Новий репетитор'}
        isEdit={Boolean(selectedTutor)}
        showSubjects
      />

      <ConfirmDialog
        open={Boolean(confirmState)}
        title="Видалити репетитора?"
        description={`Це дію не можна скасувати. Видалити ${confirmState?.name}?`}
        onCancel={() => setConfirmState(null)}
        onConfirm={async () => {
          await deleteMutation.mutateAsync(confirmState.id);
          setConfirmState(null);
        }}
      />

      <TutorReviewsDialog
        open={Boolean(reviewsTutor)}
        onClose={() => setReviewsTutor(null)}
        tutor={reviewsTutor}
      />
    </Stack>
  );
};

export default TutorsPage;
